import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useTopProjects, type MetricScope, type StatsFilters } from "../api";

type Props = { filters: StatsFilters; metricScope: MetricScope };

type TooltipPayloadItem = {
  payload?: {
    projectName?: string;
    spend?: number;
  };
  value?: number | string;
};

type TooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
};

const formatProjectSpend = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);

const formatAxisSpend = (value: number) => {
  const digits =
    value === 0 ? 0 : Math.abs(value) < 0.01 ? 4 : Math.abs(value) < 1 ? 3 : 2;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};

function ProjectTooltip({ active, payload, label }: TooltipProps) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const projectName = payload[0]?.payload?.projectName || label || "—";
  const spend = payload[0]?.payload?.spend ?? payload[0]?.value ?? 0;

  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-sm">
      <div className="font-medium">{projectName}</div>
      <div className="mt-1 text-muted-foreground">
        {t("usageAndCost.spendPerProject.spend")}:{" "}
        {formatProjectSpend(Number(spend))}
      </div>
    </div>
  );
}

export function SpendPerProject({ filters, metricScope }: Props) {
  const { t } = useTranslation();
  const projects = useTopProjects(filters, 5, metricScope);
  const { data = [], spend_complete: spendComplete } = projects.data ?? {};
  const { isLoading } = projects;
  const showSpend =
    projects.metricScope !== "usage" &&
    data.every((row) => typeof row.total_spend === "number");
  const chartData = data.map((row) => {
    const projectName = row.project_name || "—";
    const truncatedProjectName =
      projectName.length > 14 ? `${projectName.slice(0, 12)}..` : projectName;

    return {
      project: truncatedProjectName,
      projectName,
      spend: row.total_spend ?? 0,
    };
  });

  return (
    <SectionCard
      title={t("usageAndCost.spendPerProject.title")}
      subtitle={
        showSpend && spendComplete === false
          ? t("usageAndCost.dailySpend.partialSpend")
          : undefined
      }
      category={t("usageAndCost.categories.all")}
      bodyClassName="p-0"
    >
      <div
        className={`grid grid-cols-1 gap-0 ${showSpend ? "lg:grid-cols-2" : ""}`}
      >
        {showSpend && (
          <div className="h-[260px] w-full px-5 py-4">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 5, right: 24, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--border)"
                    opacity={0.4}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => formatAxisSpend(Number(value))}
                  />
                  <YAxis
                    type="category"
                    dataKey="project"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    cursor={{
                      fill: "var(--muted-foreground)",
                      fillOpacity: 0.08,
                    }}
                    content={<ProjectTooltip />}
                  />
                  <Bar dataKey="spend" fill="#86c9a6" radius={[4, 4, 4, 4]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        <div
          className={`max-h-[260px] mt-2 overflow-auto border-t border-border [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${showSpend ? "lg:border-l lg:border-t-0" : ""}`}
        >
          {isLoading ? (
            <div className="flex flex-col gap-2 p-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : (
            <table className="w-full min-w-[420px] text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border text-muted-foreground">
                  <th className="px-5 py-3 text-start text-xs font-medium">
                    {t("usageAndCost.spendPerProject.project")}
                  </th>
                  {showSpend && (
                    <th className="px-5 py-3 text-end text-xs font-medium">
                      {t("usageAndCost.topDeployments.spendUsd")}
                    </th>
                  )}
                  <th className="px-5 py-3 text-end text-xs font-medium">
                    {t("usageAndCost.providerUsage.successful")}
                  </th>
                  <th className="px-5 py-3 text-end text-xs font-medium">
                    {t("usageAndCost.providerUsage.failed")}
                  </th>
                  <th className="px-5 py-3 text-end text-xs font-medium">
                    {t("usageAndCost.providerUsage.tokens")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={showSpend ? 5 : 4}
                      className="px-5 py-8 text-center text-sm text-muted-foreground"
                    >
                      {t("usageAndCost.topList.noData")}
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <tr
                      key={row.project_id}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-5 py-3 text-foreground">
                        {row.project_name || "—"}
                      </td>
                      {showSpend && (
                        <td className="px-5 py-3 text-end text-foreground">
                          {formatProjectSpend(row.total_spend ?? 0)}
                        </td>
                      )}
                      <td className="px-5 py-3 text-end text-emerald-400">
                        {row.successful_requests}
                      </td>
                      <td className="px-5 py-3 text-end text-destructive">
                        {row.failed_requests}
                      </td>
                      <td className="px-5 py-3 text-end text-muted-foreground">
                        {row.total_tokens.toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
